import { useEffect, useRef } from "react";
import { useListOrders, customFetch } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { orderDisplayCode } from "@/lib/utils";
import {
  playNewOrderSound,
  getOrderSoundMode,
  unlockOrderSound,
} from "@/lib/order-sound";

// Mesmo guard do auto-print (ver use-auto-print-watcher): nunca tocar pra
// pedido com +5min de idade. Pedido novo chega em segundos; o resto e set-diff
// stale (cache, remount, refetch parcial) e tocaria som pra pedido antigo.
const MAX_AGE_MS = 5 * 60 * 1000;

// Toca um som no painel quando entra pedido novo do link publico. Roda no
// AdminLayout (toda pagina do admin), independente da impressora: o som
// funciona mesmo sem impressora conectada.
export function useNewOrderSound() {
  // Busca SO pedidos recentes, nao o historico. Antes era useListOrders(undefined)
  // — a lista inteira (com itens + avatarBase64) a cada 15s, em background, em
  // TODA pagina do admin: foi a regressao que estourou o egress do Supabase em
  // jun/2026 (~560GB de overage). O som so precisa detectar pedido novo, entao
  // basta a janela [mount - MAX_AGE_MS]; o payload cai pro essencial e o som
  // segue funcionando ate em background. dateFrom estavel via useRef pra nao
  // trocar a queryKey a cada render (senao o refetchInterval reinicia).
  const sinceRef = useRef(new Date(Date.now() - MAX_AGE_MS).toISOString());
  // Sem polling: o use-orders-realtime (WS no AdminLayout) invalida esta query
  // quando entra pedido novo; o backstop de 60s cobre o resto.
  const { data: orders = [], isLoading } = useListOrders({ dateFrom: sinceRef.current });
  const { data: storeSettings } = useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
  });
  const { toast } = useToast();
  const prevIdsRef = useRef<Set<string> | null>(null);
  const ordersList: any[] = orders as any[];

  // Destrava o audio numa interacao que vale como gesto (autoplay policy): pointerup, touchend, click e tecla; o
  // pointerdown so com mouse (no dedo ele nao conta); e, onde o navegador diz, so com a ativacao valendo
  // (navigator.userActivation). O destrave toca no mudo, que da certo ate fora de gesto, por isso o filtro. Os ouvintes
  // so saem quando destravou: se o play falhar, tenta na proxima.
  useEffect(() => {
    const eventos = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;
    const tira = () => eventos.forEach((e) => window.removeEventListener(e, unlock, true));
    const valeComoGesto = (e: Event) => {
      if (e.type === "pointerdown" && (e as PointerEvent).pointerType !== "mouse") return false;
      const ativacao = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
      return ativacao ? ativacao.isActive : true;
    };
    const unlock = (e: Event) => {
      if (!valeComoGesto(e)) return;
      unlockOrderSound().then((ok) => {
        if (ok) tira();
      });
    };
    eventos.forEach((e) => window.addEventListener(e, unlock, { capture: true, passive: true }));
    return tira;
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const currentIds = new Set<string>(ordersList.map((o: any) => o.id as string));

    // Primeira passada so semeia o baseline — nao toca pros pedidos que ja
    // existiam quando o painel abriu.
    if (prevIdsRef.current === null) {
      prevIdsRef.current = currentIds;
      return;
    }

    const newOrders = ordersList.filter((o: any) => !prevIdsRef.current!.has(o.id));
    prevIdsRef.current = currentIds;

    if (newOrders.length === 0) return;
    // Diferente do auto-print, NAO pulamos role=dev: o som e local e inofensivo
    // (sem papel/efeito externo) e o suporte/dono monitorando via conta dev
    // tambem quer ouvir pedido novo. Quem nao quiser, escolhe "desativado".
    const mode = getOrderSoundMode();
    if (mode === "off") return;

    const fresh = newOrders.filter((o: any) => {
      if (o.source === "pdv") return false; // pedido do balcao: o lojista mesmo criou
      if (o.source === "importado") return false; // historico de outro cardapio (T-0033)
      const createdAtMs = o.createdAt ? new Date(o.createdAt).getTime() : NaN;
      return !Number.isFinite(createdAtMs) || Date.now() - createdAtMs <= MAX_AGE_MS;
    });
    if (fresh.length === 0) return;

    playNewOrderSound(mode === "loop");
    const shortId = orderDisplayCode(fresh[0] as any, (storeSettings as any)?.orderCodeStyle);
    const title = fresh.length > 1 ? `${fresh.length} novos pedidos! 🛎️` : `Novo pedido #${shortId} 🛎️`;
    toast(mode === "loop" ? { title, description: "Toque na tela para silenciar" } : { title });
  }, [orders, isLoading]);
}
